import { useNavigate, useParams } from 'react-router-dom';
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import AuthService from '../../../services/auth.service';
import { Spinner, Alert } from '../../../services/notiflix.service';
import { useDispatch } from 'react-redux';
import { login } from '../../../actions/auth';
import { OpenEye, CloseEye } from '../IconProvider';
import {
  UPPERCASE_REGEX,
  NUMBER_REGEX,
  LENGTH_REGEX,
  SPECIAL_CHARS_REGEX,
} from '../../../utils/validationsRules';
import ButtonLoader from './ButtonLoader';
import { useTranslation } from 'react-i18next';
import AccountSettingsService from '../../../services/account-settings.service';
import { changeLanguage } from '../../../actions/langauge';
import i18n from '../../../utils/i18n';
const AuthPasswordHandle = ({ slug }) => {
  const { token } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [passwordError, setPasswordError] = useState(null);
  const [UserID, setUserID] = useState(null);
  const [passwordShown, setPasswordShown] = useState(false);
  const [isLoader, setLoader] = useState(false);

  const [confirmPasswordShown, setConfirmPasswordShown] = useState(false);
  useEffect(() => {
    verifyEmailToken();
  }, []);

  const { t } = useTranslation(['auth', 'common']);
  const rules = [
    {
      label: t('password_instruction'),
      pattern: UPPERCASE_REGEX,
    },
    { label: t('password_instruction_number'), pattern: NUMBER_REGEX },
    {
      label: t('password_instruction_special_char'),
      pattern: SPECIAL_CHARS_REGEX,
    },
    { label: t('password_instruction_length'), pattern: LENGTH_REGEX },
  ];
  const schema = Yup.object({
    password: Yup.string().required(t('new_password_required')),
    confirmPassword: Yup.string().oneOf(
      [Yup.ref('password'), null],
      t('passwords_must_match'),
    ),
  }).required();

  const {
    register,
    handleSubmit,
    trigger,
    clearErrors,
    formState: { errors },
    watch,
    setValue,
  } = useForm({
    resolver: yupResolver(schema),
  });

  const password = watch('password'); // Watch current password field
  const verifyEmailToken = async () => {
    let responseToken = await AuthService.verifyEmailToken(token);
    if (responseToken.status === 'failure') {
      Spinner.hide();
      Alert.error(responseToken.message);
      navigate('/login');
      return false;
    }
    setUserID(responseToken.data.id);
  };

  const onPasswordSet = async (_input) => {
    try {
      setLoader(true);
      const data = {
        new_password: _input.password,
        new_confirm_password: _input.confirmPassword,
        user_id: UserID,
      };
      let response = await AuthService.setNewPassword(data);
      if (response.status === 'failure') {
        setLoader(false);
        Alert.error(response.message);
        if (
          response.message ===
          t('set_reset_password_link_expired', { setorReset: 'Set' })
        ) {
          navigate('/login');
        }
        return false;
      }
      let authUser = {
        userId: response.data.id,
        email: response.data.email,
        profilePicture: response.data.profilePicture,
        role: response.data.role,
        token: response.data.token,
        userName: response.data.full_name,
        email_verified_at: response.data.email_verified_at,
        kyc_status: response.data.kyc_status,
        platform_agreement_status: response.data.platform_agreement_status,
        is2FAEnabled: response.data.is_two_factor_enabled,
        isChangePassword: response.data.is_change_password,
      };
      await Promise.resolve(dispatch(login(authUser)));
      setLoader(false);
      var userLang = navigator.language || navigator.userLanguage;
      let langUpdateResponse = await AccountSettingsService.updateUserLocalLang(
        {
          lang: userLang === 'nl' ? 'nl' : 'en',
        },
      );

      if (langUpdateResponse.status === 'failure') {
        Alert.error(langUpdateResponse.message);
      }
      i18n.changeLanguage(userLang === 'nl' ? 'nl' : 'en');
      await Promise.resolve(
        dispatch(changeLanguage(userLang === 'nl' ? 'nl' : 'en')),
      );
      Alert.success(response.message);
      navigate('/dashboard');
    } catch (error) {
      setLoader(false);
      Alert.error(error.message);
      return false;
    }
  };

  const onResetPassword = async (_input) => {
    try {
      setLoader(true);
      const data = {
        password: _input.password,
        confirm_password: _input.confirmPassword,
        token: token,
      };
      let response = await AuthService.resetPassword(data);
      if (response.status === 'failure') {
        setLoader(false);
        Alert.error(response.message);
        if (
          response.message ===
          t('set_reset_password_link_expired', { setorReset: 'Reset' })
        ) {
          navigate('/login');
        }
        if (response.message === 'Invalid Token') {
          navigate('/login');
        }
        return false;
      }

      setLoader(false);
      Alert.success(response.message);
      navigate('/login');
    } catch (error) {
      setLoader(false);
      Alert.error(error.message);
      return false;
    }
  };

  const validatePassword = (e) => {
    const value = e.target.value;
    setValue('password', value, { shouldValidate: true });

    const isValid =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^a-zA-Z0-9\s]).{8,}$/.test(value);
    setPasswordError(isValid);
  };
  const validateConfirmPassword = (e) => {
    const value = e.target.value;
    setValue('confirmPassword', value, { shouldValidate: true });

    if (value === password) {
      clearErrors('confirmPassword');
    } else {
      trigger('confirmPassword');
    }
  };
  return (
    <>
      <form
        onSubmit={handleSubmit(
          slug === 'passwordSet' ? onPasswordSet : onResetPassword,
        )}
      >
        <div className="relative mb-2 mt-4">
          <input
            placeholder={t('new_password')}
            type={passwordShown ? 'text' : 'password'}
            name="password"
            {...register('password', { required: true })}
            onInput={(e) => {
              validatePassword(e);
            }}
            className={`mb-1 dark:text-gray-900 pr-11
              ${errors.password?.message ? 'border-red-500 focus-within:outline-red-500' : ''}`}
          />
          <div onClick={() => setPasswordShown(passwordShown ? false : true)}>
            <div className="absolute right-0 top-1 w-10 h-10 flex justify-center items-center dark:stroke-gray-400">
              {passwordShown ? <CloseEye /> : <OpenEye />}
            </div>
          </div>
          <div className="common-error-text">{errors.password?.message}</div>
        </div>
        <div className="relative mb-2 mt-4">
          <input
            placeholder={t('confirm_new_password')}
            type={confirmPasswordShown ? 'text' : 'password'}
            name="confirmPassword"
            className={`mb-1 dark:text-gray-900 pr-11
              ${errors.confirmPassword?.message ? 'border-red-500 focus-within:outline-red-500' : ''}
              `}
            {...register('confirmPassword', { required: true })}
            onChange={(e) => {
              validateConfirmPassword(e); // Trigger validation for confirmPassword field
            }}
          />
          <div
            onClick={() =>
              setConfirmPasswordShown(confirmPasswordShown ? false : true)
            }
          >
            <div className="absolute right-0 top-1 w-10 h-10 flex justify-center items-center dark:stroke-gray-400">
              {confirmPasswordShown ? <CloseEye /> : <OpenEye />}
            </div>
          </div>
          <div className="common-error-text">
            {errors.confirmPassword?.message}
          </div>
        </div>
        <div
          className={
            passwordError
              ? 'p-2'
              : 'flex flex-wrap gap-x-3 p-2 border border-red-500'
          }
        >
          <p className="font-semibold">{t('password_need_to')}</p>
          {rules.map((rule, index) => {
            const cn =
              password && password.match(rule.pattern)
                ? 'text-green-500 text-[13px]'
                : password
                  ? 'text-red-500 '
                  : 'text-[13px]';
            return (
              <p className={cn} key={index}>
                {password && password.match(rule.pattern)
                  ? '✔ '
                  : password
                    ? '× '
                    : '• '}
                {rule.label}
              </p>
            );
          })}
        </div>

        <div className="mt-6">
          <button
            type="submit"
            disabled={
              isLoader || passwordError ? false : password ? true : false
            }
            className={
              passwordError
                ? ' text-[#0B243F] text-lg font-semibold rounded-md px-5 leading-normal py-3 flex flex-col justify-center items-center w-full    bg-[#A7D760] hover:bg-[#97C05B] focus:bg-[#A7D760] active:bg-[#8DB158] hover:transition-all'
                : password
                  ? 'text-[#0B243F] text-lg font-semibold rounded-md px-5 leading-normal py-3 flex flex-col justify-center items-center w-full bg-gray-400 cursor-not-allowed'
                  : 'text-[#0B243F] text-lg font-semibold rounded-md px-5 leading-normal py-3 flex flex-col justify-center items-center w-full    bg-[#A7D760] hover:bg-[#97C05B] focus:bg-[#A7D760] active:bg-[#8DB158] hover:transition-all'
            }
          >
            {isLoader ? (
              <ButtonLoader title={t('processing')} />
            ) : slug === 'passwordSet' ? (
              t('common:set_password')
            ) : (
              t('common:reset_password')
            )}
          </button>
        </div>
      </form>
    </>
  );
};
export default AuthPasswordHandle;
